import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import {
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaClock,
  FaPlus,
  FaTimes,
  FaChevronLeft,
} from "react-icons/fa";
import { graphqlRequest } from "../../../utils/graphqlRequest";
import "../styles/RosterTaskEditor.css";

function useAdminActor() {
  return useMemo(() => {
    const fromCookie = Cookies.get("userId");
    if (fromCookie) return { id: String(fromCookie) };
    const token = Cookies.get("accessToken");
    if (!token) return null;
    try {
      const decoded = jwtDecode(token);
      const id = decoded?.sub?.userId || decoded?.userId || "";
      return id ? { id: String(id) } : null;
    } catch {
      return null;
    }
  }, []);
}

const GET_PROJECT = `
  query FindOneProjectForRoster($id: String!) {
    findOneProject(id: $id) {
      id
      projectId
      name
      startDate
      deadLine
      startTime
      endTime
      status
      projectAddress
      booking {
        id
        fullName
        address
      }
    }
  }
`;

const TASK_USER_FIELDS = `
        id
        firstName
        lastName
        email
        username
        roles
        jobTitle
        employeeCode
`;

const GET_TASKS = `
  query FindTasksByProject($projectId: String!) {
    findTasksByProject(projectId: $projectId) {
      id
      title
      description
      date
      time
      endTime
      recurrenceUnit
      recurrenceInterval
      recurrenceCount
      recurrenceDays
      recurrenceEndDate
      rosterSeriesId
      rosterPublishedAt
      priority
      siteInstructions
      estimatedLabourHoursPerVisit
      requiredWorkerCount
      status
      user {${TASK_USER_FIELDS}}
      assignees {${TASK_USER_FIELDS}}
    }
  }
`;

const GET_TASKS_WITHOUT_ASSIGNEES = `
  query FindTasksByProjectNoAssignees($projectId: String!) {
    findTasksByProject(projectId: $projectId) {
      id
      title
      description
      date
      time
      rosterSeriesId
      rosterPublishedAt
      priority
      siteInstructions
      estimatedLabourHoursPerVisit
      requiredWorkerCount
      status
      user {${TASK_USER_FIELDS}}
    }
  }
`;

const loadRosterTasks = async (projectId) => {
  try {
    const data = await graphqlRequest(GET_TASKS, { projectId });
    return data?.findTasksByProject || [];
  } catch (err) {
    const msg = String(err?.message || "");
    if (/cannot query field/i.test(msg) && /assignees/i.test(msg)) {
      const data = await graphqlRequest(GET_TASKS_WITHOUT_ASSIGNEES, {
        projectId,
      });
      return data?.findTasksByProject || [];
    }
    throw err;
  }
};

const GET_ALL_USERS = `
  query GetAllUser($page: Int, $limit: Int) {
    getAllUser(page: $page, limit: $limit) {
      data {
        id
        firstName
        lastName
        username
        email
        roles
        jobTitle
        employeeCode
      }
    }
  }
`;

const CONTRACTOR_ROLES = ["SCHEDULER_DISPATCHER", "SUPERVISOR", "CONTRACTOR"];
const STAFF_ROLES = ["FIELD_CLEANER", "SUPERVISOR"];

const roleKey = (r) =>
  String(r || "")
    .toUpperCase()
    .replace(/[\s/-]+/g, "_");
const isStaffUser = (u) =>
  (u?.roles || []).some((r) => STAFF_ROLES.includes(roleKey(r)));

const uniqueById = (list) => {
  const seen = new Set();
  const out = [];
  for (const item of list || []) {
    if (!item?.id || seen.has(item.id)) continue;
    seen.add(item.id);
    out.push(item);
  }
  return out;
};

const uniqueIds = (ids) => {
  const seen = new Set();
  const out = [];
  for (const id of ids || []) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
};

const GET_USERS_BY_ROLES = `
  query GetUsersByRoles($page: Int, $limit: Int, $roles: [String!]) {
    getAllUser(page: $page, limit: $limit, roles: $roles) {
      data {
        id
        firstName
        lastName
        username
        email
        roles
        jobTitle
        employeeCode
      }
    }
  }
`;

const UPDATE_TASK_ROSTER = `
  mutation UpdateTaskRoster($input: UpdateTaskRosterInput!) {
    updateTaskRoster(input: $input) {
      id
      status
    }
  }
`;

const GET_TASK_OPERATIONAL_BRIEF = `
  query GetTaskOperationalBrief($taskId: String!) {
    getTaskOperationalBrief(taskId: $taskId)
  }
`;

const EMPTY_OPERATIONAL_FORM = {
  siteAccessInstructions: "",
  restrictedAccessInstructions: "",
  checklistText: "",
  chemicalsText: "",
  equipmentText: "",
  ppeInstructions: "",
  hazardInstructions: "",
  clientSpecialRequests: "",
  beforePhotos: false,
  afterPhotos: false,
  photoNotes: "",
  supervisorInspectionRequired: false,
  supervisorInspectionNotes: "",
};

const listToText = (value) =>
  (Array.isArray(value) ? value : []).filter(Boolean).join("\n");

const checklistTextFromSite = (site) =>
  (Array.isArray(site?.checklist) ? site.checklist : [])
    .map((item) => [item?.area, item?.task, item?.frequency].filter(Boolean).join(" | "))
    .join("\n");

const operationalFormFromBrief = (site = {}, restricted = "") => ({
  ...EMPTY_OPERATIONAL_FORM,
  siteAccessInstructions: site?.siteAccessInstructions || "",
  restrictedAccessInstructions: restricted || "",
  checklistText: checklistTextFromSite(site),
  chemicalsText: listToText(site?.requiredChemicals),
  equipmentText: listToText(site?.requiredEquipment),
  ppeInstructions: site?.ppeInstructions || "",
  hazardInstructions: site?.hazardInstructions || "",
  clientSpecialRequests: site?.clientSpecialRequests || "",
  beforePhotos: Boolean(site?.photoRequirements?.before),
  afterPhotos: Boolean(site?.photoRequirements?.after),
  photoNotes: site?.photoRequirements?.notes || "",
  supervisorInspectionRequired: Boolean(site?.supervisorInspection?.required),
  supervisorInspectionNotes: site?.supervisorInspection?.notes || "",
});

const textToList = (value) =>
  String(value || "")
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);

const buildSiteInstructions = (form, checklistText = form.checklistText) => ({
  siteAccessInstructions: form.siteAccessInstructions.trim(),
  checklist: String(checklistText || "")
    .split("\n")
    .map((line) => {
      const parts = line.split("|").map((part) => part.trim()).filter(Boolean);
      if (parts.length <= 1) return { area: "", task: parts[0] || "", frequency: "" };
      return { area: parts[0], task: parts[1], frequency: parts.slice(2).join(" | ") };
    })
    .filter((item) => item.task),
  requiredChemicals: textToList(form.chemicalsText),
  requiredEquipment: textToList(form.equipmentText),
  ppeInstructions: form.ppeInstructions.trim(),
  hazardInstructions: form.hazardInstructions.trim(),
  clientSpecialRequests: form.clientSpecialRequests.trim(),
  photoRequirements: {
    before: form.beforePhotos,
    after: form.afterPhotos,
    notes: form.photoNotes.trim(),
  },
  supervisorInspection: {
    required: form.supervisorInspectionRequired,
    notes: form.supervisorInspectionNotes.trim(),
  },
});

const ASSIGN_CONTRACTORS_AND_ROSTER = `
  mutation AssignContractorsAndRoster($input: AssignContractorsAndRosterInput!) {
    assignContractorsAndRoster(input: $input) {
      id
      paymentType
      price
      status
    }
  }
`;

const UPDATE_PROJECT = `
  mutation UpdateProject($input: UpdateProjectInput!) {
    updateProject(updateProjectInput: $input) {
      id
      manager { id }
    }
  }
`;

const GET_AVAILABILITY_BY_USER_AND_MONTH = `
  query GetAvailabilityByUserAndMonth($userId: String!, $year: Int!, $month: Int!) {
    getAvailabilityByUserAndMonth(userId: $userId, year: $year, month: $month) {
      date
      type
      mode
    }
  }
`;

const GET_RESOURCES_BY_PROJECT = `
  query GetResourcesByProject($projectId: String) {
    getResourcesByProject(projectId: $projectId) {
      id
      quantityAssigned
      tool {
        id
        name
        description
      }
    }
  }
`;

const GET_ALL_CONTRACTOR_ASSIGNMENTS = `
  query GetAllContractorAssignmentsForRoster {
    getAllContractorAssignments {
      id
      status
      paymentType
      price
      contractorName
      contractorExternalId
      contractor {
        id
        firstName
        lastName
        email
        username
        roles
        jobTitle
        employeeCode
      }
      project {
        id
      }
    }
  }
`;

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "N/A";
  return d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

const formatTime = (timeStr) => {
  if (!timeStr) return "N/A";
  const [hours, minutes] = timeStr.split(":");
  const h = parseInt(hours, 10);
  if (isNaN(h)) return timeStr;
  const m = minutes || "00";
  const ampm = h >= 12 ? "PM" : "AM";
  const displayH = h % 12 || 12;
  return `${displayH.toString().padStart(2, "0")}:${m} ${ampm}`;
};

const durationHours = (startTime, endTime) => {
  const toMinutes = (value) => {
    if (!value) return null;
    const [hours, minutes] = String(value).split(":").map(Number);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
    return hours * 60 + minutes;
  };
  const start = toMinutes(startTime);
  const end = toMinutes(endTime);
  return start === null || end === null || end <= start
    ? 0
    : Math.round(((end - start) / 60) * 100) / 100;
};

const normalize = (v) =>
  String(v || "")
    .trim()
    .toLowerCase();

const getFullName = (u) =>
  `${u?.firstName || ""} ${u?.lastName || ""}`.replace(/\s+/g, " ").trim();

const toLocalYmd = (value) => {
  if (!value) return null;
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const parseYmdLocal = (ymd) => {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d);
};

/** Inclusive list of YYYY-MM-DD days from start → end (caps at 400 days). */
const eachDayYmd = (startValue, endValue) => {
  const startYmd = toLocalYmd(startValue);
  const endYmd = toLocalYmd(endValue) || startYmd;
  if (!startYmd) return [];
  let start = parseYmdLocal(startYmd);
  let end = parseYmdLocal(endYmd) || start;
  if (!start) return [];
  if (end < start) {
    const tmp = start;
    start = end;
    end = tmp;
  }
  const out = [];
  const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  let guard = 0;
  while (cur <= end && guard < 400) {
    const y = cur.getFullYear();
    const m = String(cur.getMonth() + 1).padStart(2, "0");
    const d = String(cur.getDate()).padStart(2, "0");
    out.push(`${y}-${m}-${d}`);
    cur.setDate(cur.getDate() + 1);
    guard += 1;
  }
  return out;
};

const uniqueYearMonths = (ymdList) => {
  const seen = new Set();
  const out = [];
  for (const ymd of ymdList || []) {
    const [y, m] = ymd.split("-").map(Number);
    const key = `${y}-${m}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ year: y, month: m });
  }
  return out;
};

const isFullyUnavailableOnDate = (records, ymd) => {
  if (!ymd || !Array.isArray(records)) return false;
  const hit = records.find((a) => toLocalYmd(a.date) === ymd);
  if (!hit) return false;
  return String(hit.type || "").toLowerCase() === "unavailable";
};

/** True if unavailable on any day in the job range. No record = available that day. */
const isUnavailableOnAnyJobDay = (records, jobDays) => {
  if (!jobDays?.length) return false;
  return jobDays.some((ymd) => isFullyUnavailableOnDate(records || [], ymd));
};

const chunkArray = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const makeSlot = (label) => ({
  id: `${label}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  label,
  assignee: null,
  search: "",
  pickerOpen: false,
  taskIds: [],
});

const toPoolOption = (u, pool, codePrefix, idx) => {
  if (!u?.id) return null;
  const existing = (pool || []).find((o) => o.user?.id === u.id);
  if (existing) return existing;
  const code =
    u.employeeCode || `${codePrefix}${String(idx + 1).padStart(3, "0")}`;
  return {
    user: u,
    code,
    label: `${code} · ${getFullName(u) || u.email || "User"}`,
  };
};

const peopleOnTask = (task) => {
  const seen = new Set();
  const people = [];
  for (const u of [...(task?.assignees || []), task?.user]) {
    if (!u?.id || seen.has(u.id)) continue;
    seen.add(u.id);
    people.push(u);
  }
  return people;
};

/** Group already-assigned tasks into roster slots (one slot per person; a task may appear on several). */
const buildSlotsFromAssignedTasks = (
  loadedTasks,
  pool,
  labelPrefix,
  codePrefix,
  keepPerson,
) => {
  const byUser = new Map();
  for (const t of loadedTasks || []) {
    const people = peopleOnTask(t);
    for (const person of people) {
      if (keepPerson && !keepPerson(person)) continue;
      const uid = person.id;
      if (!byUser.has(uid)) {
        byUser.set(uid, { user: person, taskIds: [] });
      }
      byUser.get(uid).taskIds.push(t.id);
    }
  }

  if (byUser.size === 0) {
    const slot = makeSlot(`${labelPrefix} 1`);
    return { slots: [slot], activeId: slot.id };
  }

  const slots = [];
  let i = 0;
  for (const [, { user: u, taskIds }] of byUser) {
    const opt = toPoolOption(u, pool, codePrefix, i);
    const slot = {
      ...makeSlot(`${labelPrefix} ${i + 1}`),
      assignee: opt,
      search: opt?.label || "",
      taskIds,
    };
    slots.push(slot);
    i += 1;
  }
  return { slots, activeId: slots[0].id };
};

/** Restore contractor slots from contractor_assignment rows (+ optional task ids). */
const buildSlotsFromContractorAssignments = (
  assignments,
  loadedTasks,
  pool,
) => {
  const active = (assignments || []).filter((a) => {
    const st = String(a?.status || "").toLowerCase();
    return st === "pending" || st === "accepted";
  });

  if (active.length === 0) {
    const slot = makeSlot("Contractor 1");
    return { slots: [slot], activeId: slot.id, paymentType: null, price: "" };
  }

  // One slot per unique contractor on this project
  const byContractor = new Map();
  active.forEach((a) => {
    const uid =
      a?.contractor?.id ||
      a?.contractorExternalId ||
      a?.contractorName ||
      a?.id;
    if (!byContractor.has(uid)) {
      byContractor.set(uid, a);
    }
  });

  const assignedTaskIdsByUser = new Map();
  (loadedTasks || []).forEach((t) => {
    peopleOnTask(t).forEach((person) => {
      const uid = person?.id;
      if (!uid) return;
      if (!assignedTaskIdsByUser.has(uid)) assignedTaskIdsByUser.set(uid, []);
      assignedTaskIdsByUser.get(uid).push(t.id);
    });
  });

  const slots = [];
  let i = 0;
  let paymentType = null;
  let price = "";
  for (const [, assignment] of byContractor) {
    const user =
      assignment.contractor ||
      pool.find(
        (o) =>
          o.user?.id === assignment.contractorExternalId ||
          o.user?.id === assignment.contractor?.id,
      )?.user ||
      null;
    const opt = user
      ? toPoolOption(user, pool, "CTR", i)
      : {
          user: {
            id: assignment.contractorExternalId || assignment.id,
            firstName: assignment.contractorName || "Contractor",
            lastName: "",
            email: "",
          },
          code: `CTR${String(i + 1).padStart(3, "0")}`,
          label: `${assignment.contractorName || "Contractor"}`,
        };
    const taskIds =
      (user?.id && assignedTaskIdsByUser.get(user.id)) ||
      (assignment.contractorExternalId &&
        assignedTaskIdsByUser.get(assignment.contractorExternalId)) ||
      [];
    slots.push({
      ...makeSlot(`Contractor ${i + 1}`),
      assignee: opt,
      search: opt?.label || "",
      taskIds: [...taskIds],
    });
    if (!paymentType && assignment.paymentType) {
      paymentType = assignment.paymentType;
    }
    if (!price && assignment.price != null) {
      price = String(assignment.price);
    }
    i += 1;
  }

  return {
    slots,
    activeId: slots[0].id,
    paymentType,
    price,
  };
};

const RosterTaskEditor = ({ isOpen, onClose, onBack, projectId }) => {
  const user = useAdminActor();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [cleaners, setCleaners] = useState([]);
  const [selectedTasks, setSelectedTasks] = useState([]); // self / single contractor fallback
  const [assignmentType, setAssignmentType] = useState("cleaner");
  const [cleanerSlots, setCleanerSlots] = useState(() => [
    makeSlot("Person 1"),
  ]);
  const [activeCleanerSlotId, setActiveCleanerSlotId] = useState(null);
  const [contractorSlots, setContractorSlots] = useState(() => [
    makeSlot("Contractor 1"),
  ]);
  const [activeContractorSlotId, setActiveContractorSlotId] = useState(null);
  const [paymentType, setPaymentType] = useState("per_project");
  const [price, setPrice] = useState("");
  const [availabilityByUserId, setAvailabilityByUserId] = useState({});
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [resources, setResources] = useState([]);
  const [rosterDate, setRosterDate] = useState("");
  const [rosterStartTime, setRosterStartTime] = useState("");
  const [rosterEndTime, setRosterEndTime] = useState("");
  const [quotedLabourHours, setQuotedLabourHours] = useState("");
  const [requiredWorkerCount, setRequiredWorkerCount] = useState(1);
  const [recurrenceUnit, setRecurrenceUnit] = useState("NONE");
  const [recurrenceInterval, setRecurrenceInterval] = useState(1);
  const [recurrenceCount, setRecurrenceCount] = useState("");
  const [recurrenceDays, setRecurrenceDays] = useState([]);
  const [recurrenceEndDate, setRecurrenceEndDate] = useState("");
  const [rosterChangeReason, setRosterChangeReason] = useState("");
  const [operationalForm, setOperationalForm] = useState(
    EMPTY_OPERATIONAL_FORM,
  );
  const [checklistByTask, setChecklistByTask] = useState({});
  const [restrictedAccessLoaded, setRestrictedAccessLoaded] = useState(false);
  const rosterAlreadyPublished = useMemo(
    () => tasks.some((task) => Boolean(task.rosterPublishedAt)),
    [tasks],
  );

  useEffect(() => {
    if (!isOpen || !projectId) return;

    const firstCleaner = makeSlot("Person 1");
    const firstContractor = makeSlot("Contractor 1");
    setSelectedTasks([]);
    setAssignmentType("cleaner");
    setCleanerSlots([firstCleaner]);
    setActiveCleanerSlotId(firstCleaner.id);
    setContractorSlots([firstContractor]);
    setActiveContractorSlotId(firstContractor.id);
    setPaymentType("per_project");
    setPrice("");
    setAvailabilityByUserId({});
    setContractors([]);
    setCleaners([]);
    setResources([]);
    setRosterDate("");
    setRosterStartTime("");
    setRosterEndTime("");
    setQuotedLabourHours("");
    setRequiredWorkerCount(1);
    setRecurrenceUnit("NONE");
    setRecurrenceInterval(1);
    setRecurrenceCount("");
    setRecurrenceDays([]);
    setRecurrenceEndDate("");
    setRosterChangeReason("");
    setOperationalForm(EMPTY_OPERATIONAL_FORM);
    setRestrictedAccessLoaded(false);

    const fetchData = async () => {
      try {
        setLoading(true);
        const [
          projectResult,
          taskResult,
          usersResult,
          contractorResult,
          cleanerResult,
          resourceResult,
          assignmentResult,
        ] = await Promise.allSettled([
          graphqlRequest(GET_PROJECT, { id: projectId }),
          loadRosterTasks(projectId),
          graphqlRequest(GET_ALL_USERS, { page: 1, limit: 100 }),
          graphqlRequest(GET_USERS_BY_ROLES, {
            page: 1,
            limit: 200,
            roles: CONTRACTOR_ROLES,
          }),
          graphqlRequest(GET_USERS_BY_ROLES, {
            page: 1,
            limit: 200,
            roles: STAFF_ROLES,
          }),
          graphqlRequest(GET_RESOURCES_BY_PROJECT, { projectId }),
          graphqlRequest(GET_ALL_CONTRACTOR_ASSIGNMENTS),
        ]);

        [
          taskResult,
          usersResult,
          contractorResult,
          cleanerResult,
          resourceResult,
          assignmentResult,
        ].forEach((result) => {
          if (result.status === "rejected") {
            console.error("Error loading roster data:", result.reason);
          }
        });

        if (projectResult.status === "rejected") {
          console.error("Error loading roster project:", projectResult.reason);
          setProject(null);
          return;
        }

        const found = projectResult.value?.findOneProject || null;
        const loadedTasks =
          taskResult.status === "fulfilled" ? taskResult.value || [] : [];
        const loadedAllUsers =
          usersResult.status === "fulfilled"
            ? usersResult.value?.getAllUser?.data || []
            : [];
        const loadedContractors =
          contractorResult.status === "fulfilled"
            ? contractorResult.value?.getAllUser?.data || []
            : [];
        const loadedCleaners =
          cleanerResult.status === "fulfilled"
            ? cleanerResult.value?.getAllUser?.data || []
            : [];
        const projectAssignments = (
          (assignmentResult.status === "fulfilled"
            ? assignmentResult.value?.getAllContractorAssignments
            : []) || []
        ).filter((a) => a?.project?.id === projectId);

        setProject(found);
        setTasks(loadedTasks);
        setChecklistByTask(
          Object.fromEntries(
            loadedTasks.map((task) => [task.id, checklistTextFromSite(task.siteInstructions)]),
          ),
        );
        const firstOperationalTask =
          loadedTasks.find((task) => task?.siteInstructions) || loadedTasks[0];
        setOperationalForm(
          operationalFormFromBrief(firstOperationalTask?.siteInstructions),
        );
        if (firstOperationalTask?.id) {
          try {
            const briefData = await graphqlRequest(GET_TASK_OPERATIONAL_BRIEF, {
              taskId: firstOperationalTask.id,
            });
            const brief = briefData?.getTaskOperationalBrief || {};
            setOperationalForm(
              operationalFormFromBrief(
                brief.siteInstructions || firstOperationalTask.siteInstructions,
                brief.restrictedAccessInstructions || "",
              ),
            );
            setChecklistByTask((current) => ({
              ...current,
              [firstOperationalTask.id]: checklistTextFromSite(
                brief.siteInstructions || firstOperationalTask.siteInstructions,
              ),
            }));
            setRestrictedAccessLoaded(true);
          } catch (error) {
            console.error(
              "Unable to load restricted site access details:",
              error,
            );
          }
        } else {
          setRestrictedAccessLoaded(true);
        }
        const firstScheduledTask = loadedTasks.find(
          (task) => task?.date || task?.time || task?.endTime,
        );
        setRosterDate(
          String(firstScheduledTask?.date || found?.startDate || "").slice(
            0,
            10,
          ),
        );
        setRosterStartTime(firstScheduledTask?.time || found?.startTime || "");
        setRosterEndTime(firstScheduledTask?.endTime || found?.endTime || "");
        const plannedWorkers = Math.max(
          1,
          Number(firstScheduledTask?.requiredWorkerCount) ||
            peopleOnTask(firstScheduledTask).length ||
            1,
        );
        const visitDuration = durationHours(
          firstScheduledTask?.time || found?.startTime,
          firstScheduledTask?.endTime || found?.endTime,
        );
        setRequiredWorkerCount(plannedWorkers);
        setQuotedLabourHours(
          firstScheduledTask?.estimatedLabourHoursPerVisit ??
            (visitDuration ? visitDuration * plannedWorkers : ""),
        );
        setRecurrenceUnit(
          firstScheduledTask?.recurrenceUnit ||
            (firstScheduledTask?.recurrenceDays?.length ? "WEEK" : "NONE"),
        );
        setRecurrenceInterval(firstScheduledTask?.recurrenceInterval || 1);
        setRecurrenceCount(firstScheduledTask?.recurrenceCount || "");
        setRecurrenceDays(firstScheduledTask?.recurrenceDays || []);
        setRecurrenceEndDate(
          String(
            firstScheduledTask?.recurrenceEndDate || found?.deadLine || "",
          ).slice(0, 10),
        );
        setAllUsers(loadedAllUsers);
        setContractors(loadedContractors);
        setCleaners(loadedCleaners);
        setResources(
          resourceResult.status === "fulfilled"
            ? resourceResult.value?.getResourcesByProject || []
            : [],
        );

        // Team pool: cleaners and managers/supervisors on the same list
        const cleanerList = uniqueById([
          ...loadedCleaners,
          ...loadedAllUsers.filter(isStaffUser),
          ...(user?.id ? [user] : []),
        ]);
        const contractorList =
          loadedContractors.length > 0
            ? loadedContractors
            : loadedAllUsers.filter(
                (u) =>
                  (u?.roles || []).some((role) =>
                    String(role || "")
                      .toUpperCase()
                      .includes("SCHEDULER_DISPATCHER"),
                  ) ||
                  (u?.roles || []).some((role) =>
                    String(role || "")
                      .toLowerCase()
                      .includes("contractor"),
                  ) ||
                  normalize(u?.jobTitle).includes("contractor"),
              );

        const cPool = cleanerList.map((u, idx) => {
          const code =
            u?.employeeCode || `EMP${String(idx + 1).padStart(3, "0")}`;
          return {
            user: u,
            code,
            label: `${code} · ${getFullName(u) || u.email || "Team member"}`,
          };
        });
        const ctrPool = [...contractorList]
          .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
          .map((u, idx) => {
            const code =
              u?.employeeCode || `CTR${String(idx + 1).padStart(3, "0")}`;
            return {
              user: u,
              code,
              label: `${code} · ${getFullName(u) || u.email || "Contractor"}`,
            };
          });

        const assigned = loadedTasks.filter((t) => peopleOnTask(t).length > 0);
        const assignedToMe = assigned.filter((t) => {
          const people = peopleOnTask(t);
          return people.length === 1 && people[0].id === user?.id;
        });
        const staffIds = new Set(cPool.map((o) => o.user.id));
        const contractorIds = new Set(ctrPool.map((o) => o.user.id));
        const assignedToCleaners = assigned.filter((t) =>
          peopleOnTask(t).some(
            (p) => staffIds.has(p.id) || !contractorIds.has(p.id),
          ),
        );
        const assignedToContractors = assigned.filter((t) =>
          peopleOnTask(t).some((p) => contractorIds.has(p.id)),
        );
        const hasActiveContractorAssignment = projectAssignments.some((a) => {
          const st = String(a?.status || "").toLowerCase();
          return st === "pending" || st === "accepted";
        });

        if (
          assigned.length > 0 &&
          assignedToMe.length === assigned.length &&
          assignedToCleaners.length === 0 &&
          !hasActiveContractorAssignment
        ) {
          setAssignmentType("self");
          setSelectedTasks(assignedToMe.map((t) => t.id));
        } else if (
          hasActiveContractorAssignment ||
          (assignedToContractors.length > 0 && assignedToCleaners.length === 0)
        ) {
          const restored = hasActiveContractorAssignment
            ? buildSlotsFromContractorAssignments(
                projectAssignments,
                loadedTasks,
                ctrPool,
              )
            : buildSlotsFromAssignedTasks(
                assignedToContractors,
                ctrPool,
                "Contractor",
                "CTR",
              );
          setAssignmentType("contractor");
          setContractorSlots(restored.slots);
          setActiveContractorSlotId(restored.activeId);
          if (restored.paymentType) setPaymentType(restored.paymentType);
          if (restored.price !== undefined && restored.price !== "") {
            setPrice(String(restored.price));
          }
        } else if (assigned.length > 0) {
          const restored = buildSlotsFromAssignedTasks(
            assigned,
            cPool,
            "Person",
            "EMP",
            (person) =>
              staffIds.has(person.id) || !contractorIds.has(person.id),
          );
          setAssignmentType("cleaner");
          setCleanerSlots(restored.slots);
          setActiveCleanerSlotId(restored.activeId);
        }
      } catch (err) {
        console.error("Error loading roster data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isOpen, projectId, user]);

  const jobDays = useMemo(() => {
    if (!project) return [];
    const start = project.startDate || project.deadLine;
    const end = project.deadLine || project.startDate;
    return eachDayYmd(start, end);
  }, [project]);

  const jobMonths = useMemo(() => uniqueYearMonths(jobDays), [jobDays]);

  const jobRangeLabel = useMemo(() => {
    if (!jobDays.length) return null;
    if (jobDays.length === 1) return jobDays[0];
    return `${jobDays[0]} → ${jobDays[jobDays.length - 1]} (${jobDays.length} days)`;
  }, [jobDays]);

  const plannedRosterHours =
    durationHours(rosterStartTime, rosterEndTime) *
    Math.max(1, Number(requiredWorkerCount) || 1);
  const quotedHoursNumber =
    quotedLabourHours === "" ? null : Number(quotedLabourHours);
  const plannedLabourVariance =
    quotedHoursNumber === null ? null : plannedRosterHours - quotedHoursNumber;
  const plannedVariancePercent =
    quotedHoursNumber && plannedLabourVariance !== null
      ? (plannedLabourVariance / quotedHoursNumber) * 100
      : null;

  const hasContractorRole = (roles = []) => {
    const r = roles || [];
    return (
      r.some((role) =>
        String(role || "")
          .toUpperCase()
          .includes("SCHEDULER_DISPATCHER"),
      ) ||
      r.some((role) =>
        String(role || "")
          .toLowerCase()
          .includes("contractor"),
      )
    );
  };

  const contractorCandidatesWithDisplayId = useMemo(() => {
    const source =
      contractors && contractors.length > 0
        ? contractors
        : (allUsers || []).filter(
            (u) =>
              hasContractorRole(u?.roles) ||
              normalize(u?.jobTitle).includes("contractor"),
          );
    const list = [...source].sort((a, b) =>
      a.id < b.id ? -1 : a.id > b.id ? 1 : 0,
    );
    return list.map((u, idx) => ({
      user: u,
      displayId: u?.employeeCode || `CTR${String(idx + 1).padStart(3, "0")}`,
    }));
  }, [contractors, allUsers]);

  const cleanerCandidates = useMemo(() => {
    const fromApi = cleaners && cleaners.length > 0 ? cleaners : [];
    const fromAll = (allUsers || []).filter(isStaffUser);
    return uniqueById([...fromApi, ...fromAll, ...(user?.id ? [user] : [])]);
  }, [cleaners, allUsers, user]);

  const cleanerPool = useMemo(
    () =>
      cleanerCandidates.map((u, idx) => {
        const code =
          u?.employeeCode || `EMP${String(idx + 1).padStart(3, "0")}`;
        return {
          user: u,
          code,
          label: `${code} · ${getFullName(u) || u.email || "Team member"}`,
        };
      }),
    [cleanerCandidates],
  );

  const contractorPool = useMemo(
    () =>
      contractorCandidatesWithDisplayId.map((c) => ({
        user: c.user,
        code: c.displayId,
        label: `${c.displayId} · ${getFullName(c.user) || c.user.email || "Contractor"}`,
      })),
    [contractorCandidatesWithDisplayId],
  );

  const assigneePool =
    assignmentType === "contractor" ? contractorPool : cleanerPool;

  const allPoolIdsKey = useMemo(() => {
    const ids = [...cleanerPool, ...contractorPool]
      .map((o) => o.user.id)
      .filter(Boolean);
    return [...new Set(ids)].join(",");
  }, [cleanerPool, contractorPool]);

  useEffect(() => {
    if (!isOpen || !jobDays.length || !allPoolIdsKey) {
      setAvailabilityByUserId({});
      return undefined;
    }
    const users = [...cleanerPool, ...contractorPool]
      .map((o) => o.user)
      .filter(
        (u, i, arr) => u?.id && arr.findIndex((x) => x.id === u.id) === i,
      );
    let cancelled = false;

    const run = async () => {
      setLoadingAvailability(true);
      const map = {};
      try {
        for (const chunk of chunkArray(users, 8)) {
          if (cancelled) return;
          const results = await Promise.all(
            chunk.map(async (u) => {
              try {
                const monthRows = await Promise.all(
                  jobMonths.map(async ({ year, month }) => {
                    const data = await graphqlRequest(
                      GET_AVAILABILITY_BY_USER_AND_MONTH,
                      {
                        userId: u.id,
                        year,
                        month,
                      },
                    );
                    return data?.getAvailabilityByUserAndMonth || [];
                  }),
                );
                return [u.id, monthRows.flat()];
              } catch {
                return [u.id, []];
              }
            }),
          );
          results.forEach(([id, rows]) => {
            map[id] = rows;
          });
        }
        if (!cancelled) setAvailabilityByUserId(map);
      } finally {
        if (!cancelled) setLoadingAvailability(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, jobDays.join(","), allPoolIdsKey]);

  const activeSlots =
    assignmentType === "contractor" ? contractorSlots : cleanerSlots;
  const activeSlotId =
    assignmentType === "contractor"
      ? activeContractorSlotId
      : activeCleanerSlotId;
  const activeSlot =
    activeSlots.find((s) => s.id === activeSlotId) || activeSlots[0];

  const takenAssigneeIds = useMemo(() => {
    const ids = new Set();
    activeSlots.forEach((slot) => {
      if (slot.id !== activeSlot?.id && slot.assignee?.user?.id) {
        ids.add(slot.assignee.user.id);
      }
    });
    return ids;
  }, [activeSlots, activeSlot?.id]);

  const availableAssignees = useMemo(() => {
    return assigneePool.filter((opt) => {
      if (takenAssigneeIds.has(opt.user.id)) return false;
      const records = availabilityByUserId[opt.user.id];
      if (loadingAvailability && records === undefined) return true;
      return !isUnavailableOnAnyJobDay(records || [], jobDays);
    });
  }, [
    assigneePool,
    availabilityByUserId,
    jobDays,
    loadingAvailability,
    takenAssigneeIds,
  ]);

  const slotSearch = activeSlot?.search || "";
  const filteredAssignees = useMemo(() => {
    const q = normalize(slotSearch);
    if (!q) return availableAssignees;
    return availableAssignees.filter((opt) => {
      const hay = normalize(
        `${opt.code} ${opt.label} ${opt.user.email || ""} ${opt.user.username || ""}`,
      );
      return hay.includes(q);
    });
  }, [availableAssignees, slotSearch]);

  const updateActiveSlot = (patch) => {
    if (!activeSlot) return;
    if (assignmentType === "contractor") {
      setContractorSlots((prev) =>
        prev.map((s) => (s.id === activeSlot.id ? { ...s, ...patch } : s)),
      );
    } else {
      setCleanerSlots((prev) =>
        prev.map((s) => (s.id === activeSlot.id ? { ...s, ...patch } : s)),
      );
    }
  };

  const handleSelectAssignee = (opt) => {
    updateActiveSlot({ assignee: opt, search: opt.label, pickerOpen: false });
  };

  const handleAssignmentTypeChange = (type) => {
    setAssignmentType(type);
    setSelectedTasks([]);
  };

  const handleTaskToggle = (taskId) => {
    if (assignmentType === "self") {
      setSelectedTasks((prev) =>
        prev.includes(taskId)
          ? prev.filter((id) => id !== taskId)
          : [...prev, taskId],
      );
      return;
    }
    if (!activeSlot) return;
    const nextIds = activeSlot.taskIds.includes(taskId)
      ? activeSlot.taskIds.filter((id) => id !== taskId)
      : [...activeSlot.taskIds, taskId];
    updateActiveSlot({ taskIds: nextIds });
  };

  const handleAddCleaner = () => {
    const next = makeSlot(`Person ${cleanerSlots.length + 1}`);
    setCleanerSlots((prev) => [...prev, next]);
    setActiveCleanerSlotId(next.id);
  };

  const handleAddContractor = () => {
    const next = makeSlot(`Contractor ${contractorSlots.length + 1}`);
    setContractorSlots((prev) => [...prev, next]);
    setActiveContractorSlotId(next.id);
  };

  const removeSlot = (slotId) => {
    if (assignmentType === "cleaner") {
      if (cleanerSlots.length <= 1) return;
      const next = cleanerSlots.filter((s) => s.id !== slotId);
      setCleanerSlots(next);
      if (activeCleanerSlotId === slotId) setActiveCleanerSlotId(next[0].id);
    } else if (assignmentType === "contractor") {
      if (contractorSlots.length <= 1) return;
      const next = contractorSlots.filter((s) => s.id !== slotId);
      setContractorSlots(next);
      if (activeContractorSlotId === slotId)
        setActiveContractorSlotId(next[0].id);
    }
  };

  const schedulePayload = (taskId) => {
    const repeating = recurrenceUnit !== "NONE";
    return {
      ...(rosterDate ? { date: rosterDate } : {}),
      ...(rosterStartTime ? { startTime: rosterStartTime } : {}),
      ...(rosterEndTime ? { endTime: rosterEndTime } : {}),
      recurrenceUnit,
      recurrenceInterval: repeating
        ? Math.max(1, Number(recurrenceInterval) || 1)
        : 1,
      recurrenceCount:
        repeating && recurrenceCount
          ? Math.max(1, Number(recurrenceCount))
          : null,
      recurrenceDays: recurrenceUnit === "WEEK" ? recurrenceDays : [],
      recurrenceEndDate:
        repeating && recurrenceEndDate ? recurrenceEndDate : null,
      estimatedLabourHoursPerVisit:
        quotedLabourHours === "" ? null : Number(quotedLabourHours),
      requiredWorkerCount: Math.max(1, Number(requiredWorkerCount) || 1),
      siteInstructions: buildSiteInstructions(
        operationalForm,
        checklistByTask[taskId] || "",
      ),
      ...(restrictedAccessLoaded
        ? {
            restrictedAccessInstructions:
              operationalForm.restrictedAccessInstructions.trim() || null,
          }
        : {}),
    };
  };

  const toggleRecurrenceDay = (day) => {
    setRecurrenceDays((current) =>
      current.includes(day)
        ? current.filter((value) => value !== day)
        : [...current, day],
    );
  };

  const handleAssign = async () => {
    try {
      if (rosterAlreadyPublished && !rosterChangeReason.trim()) {
        toast.error("Explain why the published roster is being changed.");
        return;
      }
      setAssigning(true);

      if (assignmentType === "self") {
        if (selectedTasks.length === 0) {
          toast.error("Select at least one task.");
          return;
        }
        await Promise.all([
          graphqlRequest(UPDATE_PROJECT, {
            input: { id: projectId, manager: user?.id, status: "active" },
          }),
          graphqlRequest(UPDATE_TASK_ROSTER, {
            input: {
              projectId,
              combineTasksIntoVisit:
                !rosterAlreadyPublished ||
                tasks.some((task) => Boolean(task.rosterSeriesId)),
              changeReason: rosterChangeReason.trim() || null,
              assignments: selectedTasks.map((taskId) => {
                const task = tasks.find((item) => item.id === taskId);
                return {
                  taskId,
                  assigneeIds: uniqueIds([
                    ...peopleOnTask(task).map((person) => person.id),
                    user?.id,
                  ]),
                  ...schedulePayload(taskId),
                };
              }),
            },
          }),
        ]);
        toast.success("Project assigned to yourself successfully!");
        onClose?.();
        return;
      }

      const slots =
        assignmentType === "contractor" ? contractorSlots : cleanerSlots;
      const ready = slots.filter(
        (slot) => slot.assignee?.user?.id && slot.taskIds.length > 0,
      );
      if (ready.length === 0) {
        toast.error(
          assignmentType === "contractor"
            ? "Add at least one contractor with selected tasks."
            : "Add at least one person with selected tasks.",
        );
        return;
      }
      const selectedWorkerIds = ready.map((slot) => slot.assignee.user.id);
      if (new Set(selectedWorkerIds).size !== selectedWorkerIds.length) {
        toast.error(
          "The same worker cannot be added in more than one roster slot.",
        );
        return;
      }

      const assigneeIdsByTask = new Map();
      for (const slot of ready) {
        const userId = slot.assignee.user.id;
        for (const taskId of slot.taskIds) {
          const ids = assigneeIdsByTask.get(taskId) || [];
          if (!ids.includes(userId)) ids.push(userId);
          assigneeIdsByTask.set(taskId, ids);
        }
      }

      const preservedRoleIds = new Set(
        (assignmentType === 'contractor' ? cleanerPool : contractorPool)
          .map((option) => option.user?.id)
          .filter(Boolean),
      );
      const mergeForTask = (taskId, incomingIds) => {
        const task = tasks.find((item) => item.id === taskId);
        const retainedIds = peopleOnTask(task)
          .map((person) => person.id)
          .filter((id) => preservedRoleIds.has(id));
        return uniqueIds([...retainedIds, ...incomingIds]);
      };

      const projectStartYmd = (() => {
        const raw = project?.startDate;
        if (!raw) return null;
        const match = String(raw).match(/^(\d{4}-\d{2}-\d{2})/);
        return match ? match[1] : null;
      })();
      const roster = {
        projectId,
        combineTasksIntoVisit:
          !rosterAlreadyPublished ||
          tasks.some((task) => Boolean(task.rosterSeriesId)),
        changeReason: rosterChangeReason.trim() || null,
        assignments: [...assigneeIdsByTask].map(([taskId, ids]) => ({
          taskId,
          assigneeIds: mergeForTask(taskId, ids),
          ...(projectStartYmd && !rosterDate ? { date: projectStartYmd } : {}),
          ...schedulePayload(taskId),
        })),
      };

      if (assignmentType === "contractor") {
        await graphqlRequest(ASSIGN_CONTRACTORS_AND_ROSTER, {
          input: {
            contractorAssignments: ready.map((slot) => {
              const matched = slot.assignee.user;
              return {
                projectId,
                paymentType,
                price: price ? parseFloat(price) : null,
                contractorName:
                  getFullName(matched) || slot.assignee.label || null,
                contractorExternalId: matched.id,
                taskIds: slot.taskIds,
              };
            }),
            roster,
          },
        });
      } else {
        await Promise.all([
          graphqlRequest(UPDATE_TASK_ROSTER, { input: roster }),
          graphqlRequest(UPDATE_PROJECT, {
            input: { id: projectId, status: "active" },
          }),
        ]);
      }

      if (assignmentType === "contractor") {
        toast.success(
          ready.length === 1
            ? "Project assigned to contractor successfully!"
            : `Assigned to ${ready.length} contractors successfully!`,
        );
      } else {
        toast.success(
          ready.length === 1
            ? "Tasks assigned to the team successfully!"
            : `Tasks assigned to ${ready.length} people successfully!`,
        );
      }
      onClose?.();
    } catch (err) {
      console.error("Assign failed:", err);
      toast.error(err?.message || "Failed to assign. Please try again.");
    } finally {
      setAssigning(false);
    }
  };

  const currentTaskIds =
    assignmentType === "self" ? selectedTasks : activeSlot?.taskIds || [];

  const canAssign =
    assignmentType === "self"
      ? selectedTasks.length > 0
      : (assignmentType === "cleaner" ? cleanerSlots : contractorSlots).some(
          (s) => s.assignee?.user?.id && s.taskIds.length > 0,
        );

  if (!isOpen) return null;

  const clientName = project?.booking?.fullName || "N/A";
  const clientAddress =
    project?.projectAddress || project?.booking?.address || "N/A";

  return createPortal(
    <div className="wr-rte-overlay" onClick={onClose}>
      <div className="wr-rte-modal" onClick={(e) => e.stopPropagation()}>
        <button
          className="wr-rte-close-btn"
          onClick={onClose}
          type="button"
          aria-label="Close"
        >
          <FaTimes />
        </button>

        {loading ? (
          <div className="wr-rte-loading">Loading project data...</div>
        ) : !project ? (
          <div className="wr-rte-loading">Project not found.</div>
        ) : (
          <div className="wr-rte-modal-body">
            <div className="wr-rte-header">
              {onBack ? (
                <button type="button" className="wr-rte-back" onClick={onBack}>
                  <FaChevronLeft /> Projects
                </button>
              ) : null}
              <h1 className="wr-rte-title">Assign roster</h1>
              <p className="wr-rte-project-name">{project.name}</p>
            </div>

            <div className="wr-rte-info-row">
              <div className="wr-rte-client-info">
                <h2 className="wr-rte-client-name">{clientName}</h2>
                <div className="wr-rte-client-address">
                  <FaMapMarkerAlt className="wr-rte-icon" />
                  <span>{clientAddress}</span>
                </div>
              </div>

              <div className="wr-rte-date-group">
                <div className="wr-rte-date-box wr-rte-date-start">
                  <div className="wr-rte-date-field">
                    <span className="wr-rte-date-label">Start Date</span>
                    <div className="wr-rte-date-value">
                      <FaCalendarAlt className="wr-rte-date-icon" />
                      <span>{formatDate(project.startDate)}</span>
                    </div>
                  </div>
                  <div className="wr-rte-date-field">
                    <span className="wr-rte-date-label">Start Time</span>
                    <div className="wr-rte-date-value">
                      <FaClock className="wr-rte-date-icon" />
                      <span>{formatTime(project.startTime)}</span>
                    </div>
                  </div>
                </div>
                <div className="wr-rte-date-box wr-rte-date-end">
                  <div className="wr-rte-date-field">
                    <span className="wr-rte-date-label">End Date</span>
                    <div className="wr-rte-date-value">
                      <FaCalendarAlt className="wr-rte-date-icon" />
                      <span>{formatDate(project.deadLine)}</span>
                    </div>
                  </div>
                  <div className="wr-rte-date-field">
                    <span className="wr-rte-date-label">End Time</span>
                    <div className="wr-rte-date-value">
                      <FaClock className="wr-rte-date-icon" />
                      <span>{formatTime(project.endTime)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <section
              className="wr-rte-schedule-panel"
              aria-labelledby="roster-schedule-title"
            >
              <h3 id="roster-schedule-title" className="wr-rte-section-title">
                Roster schedule
              </h3>
              <div className="wr-rte-schedule-grid">
                <label>
                  Service date
                  <input
                    type="date"
                    value={rosterDate}
                    onChange={(event) => setRosterDate(event.target.value)}
                  />
                </label>
                <label>
                  Start time
                  <input
                    type="time"
                    value={rosterStartTime}
                    onChange={(event) => setRosterStartTime(event.target.value)}
                  />
                </label>
                <label>
                  End time
                  <input
                    type="time"
                    value={rosterEndTime}
                    onChange={(event) => setRosterEndTime(event.target.value)}
                  />
                </label>
                <label>
                  Quoted labour hours / visit
                  <input
                    type="number"
                    min="0"
                    max="1000"
                    step="0.25"
                    value={quotedLabourHours}
                    placeholder="e.g. 6"
                    onChange={(event) =>
                      setQuotedLabourHours(event.target.value)
                    }
                  />
                </label>
                <label>
                  Required workers
                  <input
                    type="number"
                    min="1"
                    max="50"
                    step="1"
                    value={requiredWorkerCount}
                    onChange={(event) =>
                      setRequiredWorkerCount(event.target.value)
                    }
                  />
                </label>
                <label>
                  Frequency
                  <select
                    value={recurrenceUnit}
                    onChange={(event) => {
                      const unit = event.target.value;
                      setRecurrenceUnit(unit);
                      if (unit === "NONE") {
                        setRecurrenceCount("");
                        setRecurrenceEndDate("");
                      } else if (!recurrenceCount && !recurrenceEndDate) {
                        setRecurrenceCount("12");
                      }
                    }}
                  >
                    <option value="NONE">One-off</option>
                    <option value="DAY">Every N days</option>
                    <option value="WEEK">Every N weeks</option>
                    <option value="MONTH">Every N months</option>
                  </select>
                </label>
                <label>
                  Repeat every
                  <input
                    type="number"
                    min="1"
                    max="24"
                    value={recurrenceInterval}
                    disabled={recurrenceUnit === "NONE"}
                    onChange={(event) =>
                      setRecurrenceInterval(event.target.value)
                    }
                  />
                </label>
                <label>
                  Stop after visits
                  <input
                    type="number"
                    min="1"
                    max="500"
                    value={recurrenceCount}
                    disabled={recurrenceUnit === "NONE"}
                    placeholder="e.g. 3"
                    onChange={(event) => setRecurrenceCount(event.target.value)}
                  />
                </label>
                <label>
                  Or repeat until
                  <input
                    type="date"
                    value={recurrenceEndDate}
                    disabled={recurrenceUnit === "NONE"}
                    onChange={(event) =>
                      setRecurrenceEndDate(event.target.value)
                    }
                  />
                </label>
              </div>
              <div className="wr-rte-labour-preview">
                <div>
                  <span>Quoted labour</span>
                  <strong>
                    {quotedHoursNumber === null
                      ? "Not set"
                      : `${quotedHoursNumber.toFixed(2)}h`}
                  </strong>
                </div>
                <div>
                  <span>Planned roster</span>
                  <strong>{plannedRosterHours.toFixed(2)}h</strong>
                </div>
                <div
                  className={
                    plannedLabourVariance > 0
                      ? "over"
                      : plannedLabourVariance < 0
                        ? "under"
                        : ""
                  }
                >
                  <span>Budget variance</span>
                  <strong>
                    {plannedLabourVariance === null
                      ? "Set quoted hours"
                      : `${plannedLabourVariance >= 0 ? "+" : ""}${plannedLabourVariance.toFixed(2)}h${plannedVariancePercent === null ? "" : ` / ${Math.abs(plannedVariancePercent).toFixed(0)}% ${plannedLabourVariance > 0 ? "over" : "under"}`}`}
                  </strong>
                </div>
              </div>
              {recurrenceUnit === "WEEK" && (
                <div
                  className="wr-rte-weekdays"
                  aria-label="Weekly repeat days"
                >
                  {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map(
                    (day) => (
                      <button
                        key={day}
                        type="button"
                        className={recurrenceDays.includes(day) ? "active" : ""}
                        aria-pressed={recurrenceDays.includes(day)}
                        onClick={() => toggleRecurrenceDay(day)}
                      >
                        {day}
                      </button>
                    ),
                  )}
                </div>
              )}
              <p className="wr-rte-schedule-help">
                Choose any weekdays for weekly rosters. Use every 2 weeks for
                fortnightly, every 3 months for quarterly, or every 6 months for
                half-yearly. Set visits, an end date, or both.
              </p>
            </section>

            <section
              className="wr-rte-operations-panel"
              aria-labelledby="site-brief-title"
            >
              <div className="wr-rte-operations-heading">
                <div>
                  <h3 id="site-brief-title" className="wr-rte-section-title">
                    Site instructions & cleaning checklist
                  </h3>
                  <p>
                    These instructions travel with every selected roster visit
                    and are shown only to authorised managers and assigned
                    workers.
                  </p>
                </div>
                <span>Operational brief</span>
              </div>

              <div className="wr-rte-operations-grid">
                <label className="wr-rte-field-wide">
                  Site access instructions
                  <textarea
                    rows={3}
                    maxLength={4000}
                    value={operationalForm.siteAccessInstructions}
                    placeholder="Entry point, parking, loading dock, sign-in process and contact on arrival"
                    onChange={(event) =>
                      setOperationalForm((current) => ({
                        ...current,
                        siteAccessInstructions: event.target.value,
                      }))
                    }
                  />
                </label>
                <label className="wr-rte-field-wide wr-rte-restricted-field">
                  Keys, lockbox and alarm details
                  <textarea
                    rows={3}
                    maxLength={4000}
                    disabled={!restrictedAccessLoaded}
                    value={operationalForm.restrictedAccessInstructions}
                    placeholder={
                      restrictedAccessLoaded
                        ? "Restricted: key location, lockbox code, alarm arm/disarm steps"
                        : "Loading restricted access details…"
                    }
                    onChange={(event) =>
                      setOperationalForm((current) => ({
                        ...current,
                        restrictedAccessInstructions: event.target.value,
                      }))
                    }
                  />
                  <small>
                    Stored outside normal task queries and released only after
                    an assignment and company/branch access check.
                  </small>
                </label>
                <div className="wr-rte-field-wide">
                  <span>Cleaning checklist</span>
                  <small>One step per line on each task. Area | Step | Every visit is optional.</small>
                  {tasks.length === 0 ? (
                    <small>No tasks on this project yet.</small>
                  ) : tasks.map((task) => (
                    <label key={task.id}>
                      {task.title || "Task"}
                      <textarea
                        rows={3}
                        value={checklistByTask[task.id] || ""}
                        placeholder="Wipe walls and glasses"
                        onChange={(event) =>
                          setChecklistByTask((current) => ({
                            ...current,
                            [task.id]: event.target.value,
                          }))
                        }
                      />
                    </label>
                  ))}
                </div>
                <label>
                  Required chemicals
                  <textarea
                    rows={4}
                    value={operationalForm.chemicalsText}
                    placeholder={
                      "Food-safe sanitiser\nHospital-grade disinfectant"
                    }
                    onChange={(event) =>
                      setOperationalForm((current) => ({
                        ...current,
                        chemicalsText: event.target.value,
                      }))
                    }
                  />
                </label>
                <label>
                  Required equipment
                  <textarea
                    rows={4}
                    value={operationalForm.equipmentText}
                    placeholder={
                      "Colour-coded mop set\nHEPA vacuum\nWet floor signs"
                    }
                    onChange={(event) =>
                      setOperationalForm((current) => ({
                        ...current,
                        equipmentText: event.target.value,
                      }))
                    }
                  />
                </label>
                <label>
                  PPE instructions
                  <textarea
                    rows={4}
                    value={operationalForm.ppeInstructions}
                    placeholder="Gloves, eye protection, non-slip footwear, mask requirements"
                    onChange={(event) =>
                      setOperationalForm((current) => ({
                        ...current,
                        ppeInstructions: event.target.value,
                      }))
                    }
                  />
                </label>
                <label>
                  Hazards and controls
                  <textarea
                    rows={4}
                    value={operationalForm.hazardInstructions}
                    placeholder="Sharps, hot surfaces, biohazards, chemicals, slip risks and isolation steps"
                    onChange={(event) =>
                      setOperationalForm((current) => ({
                        ...current,
                        hazardInstructions: event.target.value,
                      }))
                    }
                  />
                </label>
                <label className="wr-rte-field-wide">
                  Client special requests
                  <textarea
                    rows={3}
                    value={operationalForm.clientSpecialRequests}
                    placeholder="Noise restrictions, rooms not to enter, linen setup, bin placement or presentation standards"
                    onChange={(event) =>
                      setOperationalForm((current) => ({
                        ...current,
                        clientSpecialRequests: event.target.value,
                      }))
                    }
                  />
                </label>
              </div>

              <div className="wr-rte-evidence-grid">
                <div className="wr-rte-evidence-card">
                  <h4>Photo evidence</h4>
                  <label className="wr-rte-check-row">
                    <input
                      type="checkbox"
                      checked={operationalForm.beforePhotos}
                      onChange={(event) =>
                        setOperationalForm((current) => ({
                          ...current,
                          beforePhotos: event.target.checked,
                        }))
                      }
                    />
                    Before photos required
                  </label>
                  <label className="wr-rte-check-row">
                    <input
                      type="checkbox"
                      checked={operationalForm.afterPhotos}
                      onChange={(event) =>
                        setOperationalForm((current) => ({
                          ...current,
                          afterPhotos: event.target.checked,
                        }))
                      }
                    />
                    After photos required
                  </label>
                  <textarea
                    rows={2}
                    value={operationalForm.photoNotes}
                    placeholder="Required angles, rooms or proof points"
                    onChange={(event) =>
                      setOperationalForm((current) => ({
                        ...current,
                        photoNotes: event.target.value,
                      }))
                    }
                  />
                </div>
                <div className="wr-rte-evidence-card">
                  <h4>Supervisor inspection</h4>
                  <label className="wr-rte-check-row">
                    <input
                      type="checkbox"
                      checked={operationalForm.supervisorInspectionRequired}
                      onChange={(event) =>
                        setOperationalForm((current) => ({
                          ...current,
                          supervisorInspectionRequired: event.target.checked,
                        }))
                      }
                    />
                    Inspection required before close-out
                  </label>
                  <textarea
                    rows={3}
                    value={operationalForm.supervisorInspectionNotes}
                    placeholder="Inspection standard, supervisor contact and sign-off notes"
                    onChange={(event) =>
                      setOperationalForm((current) => ({
                        ...current,
                        supervisorInspectionNotes: event.target.value,
                      }))
                    }
                  />
                </div>
              </div>
            </section>

            {rosterAlreadyPublished && (
              <section
                className="wr-rte-roster-change"
                aria-labelledby="roster-change-title"
              >
                <h3 id="roster-change-title" className="wr-rte-section-title">
                  Published roster change
                </h3>
                <p>
                  This roster is already live. Explain the operational reason
                  for changing its schedule or assignments. Completed and
                  attendance-linked visits remain locked.
                </p>
                <label htmlFor="roster-change-reason">
                  Change reason
                  <textarea
                    id="roster-change-reason"
                    value={rosterChangeReason}
                    maxLength={1000}
                    required
                    rows={3}
                    placeholder="For example: approved leave coverage or client schedule change"
                    onChange={(event) =>
                      setRosterChangeReason(event.target.value)
                    }
                  />
                </label>
              </section>
            )}

            <h3 className="wr-rte-section-title">Assigned Project</h3>

            <div className="wr-rte-assign-tabs">
              <button
                className={`wr-rte-assign-tab wr-rte-tab-cleaner ${assignmentType === "cleaner" ? "active" : ""}`}
                onClick={() => handleAssignmentTypeChange("cleaner")}
                type="button"
              >
                Assign to Team
              </button>
              <button
                className={`wr-rte-assign-tab wr-rte-tab-contractor ${assignmentType === "contractor" ? "active" : ""}`}
                onClick={() => handleAssignmentTypeChange("contractor")}
                type="button"
              >
                Assign to Contractor
              </button>
              <button
                className={`wr-rte-assign-tab wr-rte-tab-self ${assignmentType === "self" ? "active" : ""}`}
                onClick={() => handleAssignmentTypeChange("self")}
                type="button"
              >
                Assign to My Self
              </button>
            </div>

            {assignmentType === "cleaner" && (
              <div className="wr-rte-cleaner-tabs">
                {cleanerSlots.map((slot) => (
                  <button
                    key={slot.id}
                    type="button"
                    className={`wr-rte-cleaner-tab ${activeCleanerSlotId === slot.id ? "active" : ""}`}
                    onClick={() => setActiveCleanerSlotId(slot.id)}
                  >
                    {slot.label}
                    {slot.assignee ? ` · ${slot.assignee.code}` : ""}
                    {slot.taskIds.length > 0 ? ` (${slot.taskIds.length})` : ""}
                    {cleanerSlots.length > 1 && (
                      <span
                        className="wr-rte-tab-remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeSlot(slot.id);
                        }}
                        role="presentation"
                      >
                        ×
                      </span>
                    )}
                  </button>
                ))}
                <button
                  className="wr-rte-cleaner-tab wr-rte-add-cleaner"
                  type="button"
                  onClick={handleAddCleaner}
                >
                  <FaPlus className="wr-rte-add-icon" /> Add person
                </button>
              </div>
            )}

            {assignmentType === "contractor" && (
              <>
                <div className="wr-rte-cleaner-tabs">
                  {contractorSlots.map((slot) => (
                    <button
                      key={slot.id}
                      type="button"
                      className={`wr-rte-cleaner-tab ${activeContractorSlotId === slot.id ? "active" : ""}`}
                      onClick={() => setActiveContractorSlotId(slot.id)}
                    >
                      {slot.label}
                      {slot.assignee ? ` · ${slot.assignee.code}` : ""}
                      {slot.taskIds.length > 0
                        ? ` (${slot.taskIds.length})`
                        : ""}
                      {contractorSlots.length > 1 && (
                        <span
                          className="wr-rte-tab-remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeSlot(slot.id);
                          }}
                          role="presentation"
                        >
                          ×
                        </span>
                      )}
                    </button>
                  ))}
                  <button
                    className="wr-rte-cleaner-tab wr-rte-add-cleaner"
                    type="button"
                    onClick={handleAddContractor}
                  >
                    <FaPlus className="wr-rte-add-icon" /> Add Contractor
                  </button>
                </div>

                <div className="wr-rte-payment-row">
                  <button
                    type="button"
                    className={`wr-rte-payment-btn ${paymentType === "per_project" ? "active" : ""}`}
                    onClick={() => setPaymentType("per_project")}
                  >
                    Pay per Project
                  </button>
                  <button
                    type="button"
                    className={`wr-rte-payment-btn ${paymentType === "per_hour" ? "active" : ""}`}
                    onClick={() => setPaymentType("per_hour")}
                  >
                    Pay Per Hour
                  </button>
                </div>

                <div className="wr-rte-price-field">
                  <label className="wr-rte-field-label">Price</label>
                  <input
                    type="text"
                    className="wr-rte-field-input wr-rte-price-input"
                    placeholder="Enter Price"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                  />
                </div>
              </>
            )}

            {assignmentType !== "self" && activeSlot && (
              <div className="wr-rte-assignee-picker">
                <label className="wr-rte-field-label">
                  {assignmentType === "contractor"
                    ? "Select Contractor"
                    : "Select team member"}
                  <span className="wr-rte-avail-hint">
                    {" "}
                    for {activeSlot.label}
                    {jobRangeLabel
                      ? ` · available for ${jobRangeLabel}${loadingAvailability ? " (checking…)" : ""}`
                      : " · set project start/end dates to filter by availability"}
                  </span>
                </label>
                <div className="wr-rte-picker-wrap">
                  <input
                    type="text"
                    className="wr-rte-field-input wr-rte-picker-input"
                    placeholder={
                      assignmentType === "contractor"
                        ? "Search contractor ID or name…"
                        : "Search employee ID or name…"
                    }
                    value={activeSlot.search}
                    onChange={(e) =>
                      updateActiveSlot({
                        search: e.target.value,
                        assignee: null,
                        pickerOpen: true,
                      })
                    }
                    onFocus={() => updateActiveSlot({ pickerOpen: true })}
                    onBlur={() => {
                      window.setTimeout(
                        () => updateActiveSlot({ pickerOpen: false }),
                        150,
                      );
                    }}
                    autoComplete="off"
                  />
                  {activeSlot.pickerOpen && (
                    <div className="wr-rte-picker-dropdown">
                      {filteredAssignees.length === 0 ? (
                        <div className="wr-rte-picker-empty">
                          {loadingAvailability
                            ? "Loading availability…"
                            : "No available people match your search."}
                        </div>
                      ) : (
                        filteredAssignees.map((opt) => (
                          <button
                            key={opt.user.id}
                            type="button"
                            className={`wr-rte-picker-option ${
                              activeSlot.assignee?.user?.id === opt.user.id
                                ? "selected"
                                : ""
                            }`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleSelectAssignee(opt)}
                          >
                            <span className="wr-rte-picker-code">
                              {opt.code}
                            </span>
                            <span className="wr-rte-picker-name">
                              {getFullName(opt.user) || opt.user.email || "—"}
                            </span>
                            <span className="wr-rte-picker-badge">
                              Available
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                {activeSlot.assignee && (
                  <p className="wr-rte-picker-selected">
                    Selected for {activeSlot.label}:{" "}
                    <strong>{activeSlot.assignee.label}</strong>
                  </p>
                )}
                <p className="wr-rte-slot-hint">
                  Pick tasks for this person. The same task can be ticked for
                  cleaners, managers, and contractors when the job needs extra
                  people.
                </p>
              </div>
            )}

            {assignmentType === "self" && (
              <p className="wr-rte-slot-hint">
                Tick tasks to add yourself. Other people already on those tasks
                stay assigned.
              </p>
            )}

            <div className="wr-rte-task-section">
              <h3 className="wr-rte-task-heading">
                Select Task
                {assignmentType !== "self" && activeSlot
                  ? ` · ${activeSlot.label}`
                  : ""}
              </h3>
              <div className="wr-rte-task-list">
                {tasks.length === 0 ? (
                  <p className="wr-rte-no-tasks">
                    No tasks found for this project.
                  </p>
                ) : (
                  tasks.map((task) => {
                    const checked = currentTaskIds.includes(task.id);
                    const alsoOn = activeSlots
                      .filter(
                        (s) =>
                          s.id !== activeSlot?.id &&
                          (s.taskIds || []).includes(task.id),
                      )
                      .map((s) => s.assignee?.label || s.label)
                      .filter(Boolean);
                    const alreadyOn = peopleOnTask(task)
                      .map(
                        (u) =>
                          `${u.firstName || ""} ${u.lastName || ""}`.trim() ||
                          u.email,
                      )
                      .filter(Boolean);
                    return (
                      <label key={task.id} className="wr-rte-task-item">
                        <input
                          type="checkbox"
                          className="wr-rte-task-checkbox"
                          checked={checked}
                          onChange={() => handleTaskToggle(task.id)}
                        />
                        <div className="wr-rte-task-content">
                          <span className="wr-rte-task-title">
                            {task.title}
                          </span>
                          <span className="wr-rte-task-desc">
                            {alsoOn.length
                              ? `Also on ${alsoOn.join(", ")}`
                              : alreadyOn.length
                                ? `Assigned: ${alreadyOn.join(", ")}`
                                : String(task.description || "")
                                    .split("\n")
                                    .map((line) => line.trim())
                                    .filter(
                                      (line) =>
                                        line &&
                                        !/^(Service|Requirement|Role|Frequency):/i.test(line),
                                    )
                                    .join("\n") ||
                                  `${task.date || ""} ${task.time || ""} · Priority: ${task.priority || "N/A"}`}
                          </span>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
            </div>

            <div className="wr-rte-resources-section">
              <div className="wr-rte-resources-header">
                <h3 className="wr-rte-section-title">Assigned Resources</h3>
                <span className="wr-rte-resources-count">
                  {resources.length} item{resources.length === 1 ? "" : "s"}
                </span>
              </div>
              {resources.length === 0 ? (
                <p className="wr-rte-resources-empty">
                  No resources assigned yet.
                </p>
              ) : (
                <div className="wr-rte-resources-list">
                  {resources.map((r) => (
                    <div key={r.id} className="wr-rte-resource-item">
                      <div className="wr-rte-resource-info">
                        <span className="wr-rte-resource-name">
                          {r.tool?.name || "Unknown"}
                        </span>
                        {r.tool?.description ? (
                          <span className="wr-rte-resource-desc">
                            {r.tool.description}
                          </span>
                        ) : null}
                      </div>
                      <span className="wr-rte-resource-qty">
                        {r.quantityAssigned} pcs
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="wr-rte-bottom-actions">
              <button
                type="button"
                className="wr-rte-btn-assign"
                onClick={handleAssign}
                disabled={
                  !canAssign ||
                  (rosterAlreadyPublished && !rosterChangeReason.trim()) ||
                  assigning
                }
              >
                {assigning
                  ? "Saving roster..."
                  : rosterAlreadyPublished
                    ? "Update roster"
                    : "Publish roster"}
              </button>
              <button
                type="button"
                className="wr-rte-btn-cancel"
                onClick={onClose}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default RosterTaskEditor;
